import { Component, OnInit } from "@angular/core";

import { forkJoin, of } from "rxjs";

import { catchError, finalize } from "rxjs/operators";

import { ToastrService } from "ngx-toastr";

import { ModuleConfigService } from "../module-config/module-config.service";

interface RoleItem {
  roleId: number;

  roleName: string;

  roleCode: string;

  roleDesc: string;

  groupName: string;

  moduleName: string;

  moduleId: number;

  isActive: number;

  isDeleted: number;
}

interface MenuItem {
  menuId: number;

  menuName: string;

  menuCode: string;

  menuDesc: string;

  displayOrder: number;

  parentMenuId: number;

  defaultChildMenuId: number;

  menuIconUrl: string;

  templatePath: string;

  moduleName: string;

  moduleId: number;

  isActive: number;

  isDeleted: number;
}

interface SubRoleItem {
  subRoleId: number;

  roleId: number;

  subRoleName: string;

  subRoleCode: string;

  subRoleDesc: string;

  menuId: number;

  displayOrder: number;

  isDefault: number;

  isActive: number;

  isDeleted: number;
}

interface MappingView {
  subRoleId: number;

  roleId: number;

  menuId: number;

  menuName: string;

  menuCode: string;

  menuIconUrl: string;

  parentMenuId: number;

  parentMenuName: string;

  templatePath: string;

  displayOrder: number;

  isDefault: number;

  isActive: number;

  isDeleted: number;
}

@Component({
  selector: "app-subrole-module",
  templateUrl: "./subrole-module.component.html",
  styleUrls: ["./subrole-module.component.css"],
})
export class SubroleModuleComponent implements OnInit {
  // ============================================================
  // DATA
  // ============================================================

  roles: RoleItem[] = [];

  menus: MenuItem[] = [];

  roleMappings: SubRoleItem[] = [];

  // ============================================================
  // MODULE SELECTION
  // ============================================================

  moduleOptions = ["School", "Hospital", "Society", "Common"];

  selectedModuleName: string = "";

  // ============================================================
  // SELECTION
  // ============================================================

  selectedRoleId: number = 0;

  /*
   * 0  = Not Selected
   * -1 = N/A / Select submenu directly
   * >0 = Actual Parent MenuId
   */
  selectedParentMenuId: number = 0;

  selectedSubMenuId: number = 0;

  // ============================================================
  // EDIT
  // ============================================================

  editingSubRoleId: number = 0;

  editingOriginalMenuId: number = 0;

  editingOriginalParentMenuId: number = 0;

  // ============================================================
  // SEARCH
  // ============================================================

  searchText: string = "";

  // ============================================================
  // LOADING
  // ============================================================

  loading: boolean = false;

  loadingMappings: boolean = false;

  saving: boolean = false;

  constructor(
    private service: ModuleConfigService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.loadInitialData();
  }

  // ============================================================
  // INITIAL DATA
  // ============================================================

  loadInitialData(): void {
    if (this.loading) {
      return;
    }

    this.loading = true;

    const roleRequest = {
      pageSize: 1000,

      pageNo: 1,

      whereClause: null,

      orderByClause: "A.RoleName ASC",
    };

    const menuRequest = {
      pageSize: 1000,

      pageNo: 1,

      whereClause: null,

      orderByClause: null,
    };

    console.log("Roles ReadAllPaginated Request:", roleRequest);

    console.log("MenuMaster ReadAllPaginated Request:", menuRequest);

    const rolesRequest = this.service.readAllRoles(roleRequest).pipe(
      catchError((error) => {
        console.error("Roles ReadAllPaginated Error:", error);

        this.toastr.error("Unable to load roles.");

        return of([]);
      }),
    );

    const menusRequest = this.service.readAllMenusPaginated(menuRequest).pipe(
      catchError((error) => {
        console.error("MenuMaster ReadAllPaginated Error:", error);

        this.toastr.error("Unable to load menus.");

        return of([]);
      }),
    );

    forkJoin({
      roles: rolesRequest,

      menus: menusRequest,
    })
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (response) => {
          console.log("Roles ReadAllPaginated Response:", response.roles);

          console.log("MenuMaster ReadAllPaginated Response:", response.menus);

          // ====================================================
          // ROLES
          // ====================================================

          this.roles = this.extractArray(response.roles)
            .map((item: any) => this.normalizeRole(item))
            .filter((item) => item.isDeleted === 0 && item.isActive === 1)
            .sort((a, b) =>
              String(a.roleName || "").localeCompare(String(b.roleName || "")),
            );

          // ====================================================
          // MENUS
          // ====================================================

          this.menus = this.extractArray(response.menus)
            .map((item: any) => this.normalizeMenu(item))
            .filter((item) => item.isDeleted === 0)
            .sort((a, b) => {
              const displayOrderCompare =
                Number(a.displayOrder || 0) - Number(b.displayOrder || 0);

              if (displayOrderCompare !== 0) {
                return displayOrderCompare;
              }

              return Number(a.menuId || 0) - Number(b.menuId || 0);
            });

          console.log("Sub Roles - Normalized Roles:", this.roles);

          console.log("Sub Roles - Normalized Menus:", this.menus);

          /*
           * Safety:
           * If selected role no longer belongs to selected module,
           * reset it.
           */
          if (
            this.selectedRoleId > 0 &&
            !this.filteredRoles.some(
              (role) => Number(role.roleId) === Number(this.selectedRoleId),
            )
          ) {
            this.selectedRoleId = 0;

            this.roleMappings = [];

            this.clearMenuSelection();
          }
        },
      });
  }

  // ============================================================
  // FILTER ROLES BY MODULE
  // ============================================================

  get filteredRoles(): RoleItem[] {
    if (!this.selectedModuleName) {
      return [];
    }

    return this.roles
      .filter((role) =>
        this.roleMatchesSelectedModule(role, this.selectedModuleName),
      )
      .sort((a, b) =>
        String(a.roleName || "").localeCompare(String(b.roleName || "")),
      );
  }

  // ============================================================
  // IMPORTANT FIX
  // ALWAYS GET ACTUAL SELECTED ROLE OBJECT
  // ============================================================

  get selectedRole(): RoleItem | undefined {
    if (this.selectedRoleId <= 0) {
      return undefined;
    }

    return this.roles.find(
      (role) => Number(role.roleId) === Number(this.selectedRoleId),
    );
  }

  // ============================================================
  // ACTUAL ROLE ID
  // ============================================================

  get actualSelectedRoleId(): number {
    const role = this.selectedRole;

    if (!role) {
      return 0;
    }

    return Number(role.roleId || 0);
  }

  // ============================================================
  // ACTIVE MENUS
  // ============================================================

  get activeMenus(): MenuItem[] {
    return this.menus.filter(
      (menu) => menu.isActive === 1 && menu.isDeleted === 0,
    );
  }

  // ============================================================
  // MENUS FOR SELECTED MODULE
  // ============================================================

  get moduleMenus(): MenuItem[] {
    if (!this.selectedModuleName) {
      return [];
    }

    return this.activeMenus.filter((menu) =>
      this.moduleMatches(menu.moduleName, this.selectedModuleName),
    );
  }

  // ============================================================
  // PARENT MENUS
  // ============================================================

  get parentMenus(): MenuItem[] {
    /*
     * MINIMUM FIX:
     * Keep the existing module-based parent menu list, but also include the
     * actual parent menus already related to the selected RoleId.
     *
     * Some existing MenuMaster rows have legacy/Global/blank ModuleName values.
     * The old code used only moduleMenus, so valid parents already mapped to
     * the selected role were hidden from the Parent Menu dropdown.
     *
     * No create/update/delete flow is changed here.
     */
    const result: MenuItem[] = this.moduleMenus
      .filter((menu) => Number(menu.parentMenuId || 0) === 0);

    if (this.actualSelectedRoleId > 0) {
      const roleRelatedParentIds = new Set<number>();

      this.roleMappings
        .filter(
          (mapping) =>
            Number(mapping.roleId) === Number(this.actualSelectedRoleId) &&
            mapping.isDeleted === 0,
        )
        .forEach((mapping) => {
          const mappedMenu = this.menus.find(
            (menu) => Number(menu.menuId) === Number(mapping.menuId),
          );

          if (!mappedMenu || mappedMenu.isDeleted !== 0) {
            return;
          }

          if (Number(mappedMenu.parentMenuId || 0) === 0) {
            roleRelatedParentIds.add(Number(mappedMenu.menuId));
          } else {
            roleRelatedParentIds.add(Number(mappedMenu.parentMenuId));
          }
        });

      roleRelatedParentIds.forEach((parentMenuId) => {
        const parentMenu = this.menus.find(
          (menu) =>
            Number(menu.menuId) === Number(parentMenuId) &&
            Number(menu.parentMenuId || 0) === 0 &&
            menu.isActive === 1 &&
            menu.isDeleted === 0,
        );

        if (
          parentMenu &&
          !result.some(
            (menu) => Number(menu.menuId) === Number(parentMenu.menuId),
          )
        ) {
          result.push(parentMenu);
        }
      });
    }

    /*
     * EXISTING EDIT FIX:
     * Keep the exact parent already stored in DB visible during edit.
     */
    if (this.isEditMode && this.selectedParentMenuId > 0) {
      const editParent = this.menus.find(
        (menu) =>
          Number(menu.menuId) === Number(this.selectedParentMenuId) &&
          Number(menu.parentMenuId || 0) === 0 &&
          menu.isDeleted === 0,
      );

      if (
        editParent &&
        !result.some(
          (menu) => Number(menu.menuId) === Number(editParent.menuId),
        )
      ) {
        result.push(editParent);
      }
    }

    return result.sort((a, b) => {
      const orderCompare =
        Number(a.displayOrder || 0) - Number(b.displayOrder || 0);

      if (orderCompare !== 0) {
        return orderCompare;
      }

      return String(a.menuName || "").localeCompare(
        String(b.menuName || ""),
      );
    });
  }

  // ============================================================
  // SUB MENUS
  // ============================================================

  get subMenus(): MenuItem[] {
    if (this.selectedParentMenuId === 0) {
      return [];
    }

    let result: MenuItem[] = [];

    /*
     * MINIMUM FIX:
     *
     * Proper hierarchy:
     * Module -> Role -> Parent Menu -> Sub Menu
     *
     * A submenu must now:
     * 1. belong to the selected ParentMenuId, AND
     * 2. belong to the selected Module,
     *    OR already be mapped to the selected Role for legacy DB support.
     *
     * This prevents unrelated submenu rows from appearing.
     */
    if (this.selectedParentMenuId > 0) {
      result = this.activeMenus.filter((menu) => {
        if (
          Number(menu.parentMenuId || 0) !==
          Number(this.selectedParentMenuId)
        ) {
          return false;
        }

        const belongsToSelectedModule = this.moduleMatches(
          menu.moduleName,
          this.selectedModuleName,
        );

        const alreadyMappedToSelectedRole =
          this.actualSelectedRoleId > 0 &&
          this.roleMappings.some(
            (mapping) =>
              Number(mapping.roleId) === Number(this.actualSelectedRoleId) &&
              Number(mapping.menuId) === Number(menu.menuId) &&
              mapping.isDeleted === 0,
          );

        return belongsToSelectedModule || alreadyMappedToSelectedRole;
      });
    } else if (this.selectedParentMenuId === -1) {
      /*
       * Existing N/A flow:
       * Show child menus from selected module.
       */
      result = this.moduleMenus.filter(
        (menu) => Number(menu.parentMenuId || 0) > 0,
      );

      /*
       * Legacy support:
       * Keep already-mapped child menus visible for the selected Role even
       * when old MenuMaster rows contain Global/blank ModuleName.
       */
      if (this.actualSelectedRoleId > 0) {
        this.roleMappings
          .filter(
            (mapping) =>
              Number(mapping.roleId) === Number(this.actualSelectedRoleId) &&
              mapping.isDeleted === 0,
          )
          .forEach((mapping) => {
            const mappedMenu = this.menus.find(
              (menu) =>
                Number(menu.menuId) === Number(mapping.menuId) &&
                Number(menu.parentMenuId || 0) > 0 &&
                menu.isActive === 1 &&
                menu.isDeleted === 0,
            );

            if (
              mappedMenu &&
              !result.some(
                (menu) => Number(menu.menuId) === Number(mappedMenu.menuId),
              )
            ) {
              result.push(mappedMenu);
            }
          });
      }
    }

    /*
     * EXISTING EDIT FIX:
     * Keep the exact submenu already stored in DB visible during edit.
     */
    if (this.isEditMode && this.selectedSubMenuId > 0) {
      const editSubMenu = this.menus.find(
        (menu) =>
          Number(menu.menuId) === Number(this.selectedSubMenuId) &&
          Number(menu.parentMenuId || 0) > 0 &&
          menu.isDeleted === 0,
      );

      if (
        editSubMenu &&
        !result.some(
          (menu) => Number(menu.menuId) === Number(editSubMenu.menuId),
        )
      ) {
        result.push(editSubMenu);
      }
    }

    return result.sort((a, b) => {
      if (this.selectedParentMenuId === -1) {
        const parentA = this.getMenuParentName(a.parentMenuId);
        const parentB = this.getMenuParentName(b.parentMenuId);
        const parentCompare = parentA.localeCompare(parentB);

        if (parentCompare !== 0) {
          return parentCompare;
        }
      }

      const orderCompare =
        Number(a.displayOrder || 0) - Number(b.displayOrder || 0);

      if (orderCompare !== 0) {
        return orderCompare;
      }

      return String(a.menuName || "").localeCompare(
        String(b.menuName || ""),
      );
    });
  }

  // ============================================================
  // MODULE CHANGE
  // ============================================================

  onModuleChange(): void {
    /*
     * Editing locks Module/Role from HTML.
     * This safety still keeps state correct.
     */
    if (this.isEditMode) {
      return;
    }

    this.selectedRoleId = 0;

    this.roleMappings = [];

    this.searchText = "";

    this.clearMenuSelection();
  }

  // ============================================================
  // ROLE CHANGE
  // ============================================================

  onRoleChange(): void {
    if (this.isEditMode) {
      return;
    }

    this.clearMenuSelection();

    this.roleMappings = [];

    if (this.actualSelectedRoleId <= 0) {
      return;
    }

    console.log("Selected Role:", this.selectedRole);

    console.log("Selected Actual RoleId:", this.actualSelectedRoleId);

    this.loadRoleMappings();
  }

  // ============================================================
  // PARENT MENU CHANGE
  // ============================================================

  onParentMenuChange(): void {
    this.selectedSubMenuId = 0;
  }

  // ============================================================
  // EDIT MODE
  // ============================================================

  get isEditMode(): boolean {
    return this.editingSubRoleId > 0;
  }

  // ============================================================
  // CAN SAVE
  // ============================================================

  get canAssignMenu(): boolean {
    if (
      this.saving ||
      !this.selectedModuleName ||
      this.actualSelectedRoleId <= 0
    ) {
      return false;
    }

    /*
     * Parent selected.
     * Sub menu optional.
     */
    if (this.selectedParentMenuId > 0) {
      return true;
    }

    /*
     * N/A:
     * Submenu required.
     */
    if (this.selectedParentMenuId === -1) {
      return this.selectedSubMenuId > 0;
    }

    return false;
  }

  // ============================================================
  // LOAD ROLE MAPPINGS
  // ============================================================

  loadRoleMappings(): void {
    const roleId = this.actualSelectedRoleId;

    if (roleId <= 0) {
      return;
    }

    this.loadingMappings = true;

    const request = {
      roleId: roleId,
    };

    console.log("============================================");

    console.log("SubRoles ReadByRoleId Request:", request);

    console.log("Selected Role:", this.selectedRole?.roleName);

    console.log("Selected RoleId:", roleId);

    console.log("============================================");

    this.service
      .readSubRolesByRoleId(request)
      .pipe(
        finalize(() => {
          this.loadingMappings = false;
        }),
      )
      .subscribe({
        next: (response) => {
          console.log("SubRoles ReadByRoleId Response:", response);

          this.roleMappings = this.extractArray(response).map((item: any) =>
            this.normalizeSubRole(item),
          );

          console.log("Sub Roles - Role Mappings:", this.roleMappings);
        },

        error: (error) => {
          console.error("SubRoles ReadByRoleId Error:", error);

          this.roleMappings = [];

          this.showError(error, "Unable to load role menu mappings.");
        },
      });
  }

  // ============================================================
  // MAPPED MENUS
  // ============================================================

  get mappedMenus(): MappingView[] {
    const search = String(this.searchText || "")
      .trim()
      .toLowerCase();

    const result: MappingView[] = [];

    this.roleMappings
      .filter((mapping) => mapping.isDeleted === 0)
      .forEach((mapping) => {
        const menu = this.menus.find(
          (item) => Number(item.menuId) === Number(mapping.menuId),
        );

        if (!menu) {
          return;
        }

        const parent =
          Number(menu.parentMenuId || 0) > 0
            ? this.menus.find(
                (item) => Number(item.menuId) === Number(menu.parentMenuId),
              )
            : undefined;

        const row: MappingView = {
          subRoleId: mapping.subRoleId,

          roleId: mapping.roleId,

          menuId: menu.menuId,

          menuName: menu.menuName,

          menuCode: menu.menuCode,

          menuIconUrl: menu.menuIconUrl,

          parentMenuId: menu.parentMenuId,

          parentMenuName: parent?.menuName || "Root",

          templatePath: menu.templatePath,

          displayOrder: menu.displayOrder,

          isDefault: mapping.isDefault,

          isActive: mapping.isActive,

          isDeleted: mapping.isDeleted,
        };

        if (search) {
          const match =
            String(row.menuName || "")
              .toLowerCase()
              .includes(search) ||
            String(row.menuCode || "")
              .toLowerCase()
              .includes(search) ||
            String(row.parentMenuName || "")
              .toLowerCase()
              .includes(search) ||
            String(row.templatePath || "")
              .toLowerCase()
              .includes(search);

          if (!match) {
            return;
          }
        }

        result.push(row);
      });

    return result.sort((a, b) => {
      if (a.parentMenuId === 0 && b.parentMenuId !== 0) {
        return -1;
      }

      if (a.parentMenuId !== 0 && b.parentMenuId === 0) {
        return 1;
      }

      return Number(a.displayOrder || 0) - Number(b.displayOrder || 0);
    });
  }

  // ============================================================
  // SELECTED ROLE NAME
  // ============================================================

  get selectedRoleName(): string {
    return this.selectedRole?.roleName || "";
  }

  // ============================================================
  // MENU MAPPED CHECK
  // ============================================================

  isMenuMapped(menuId: number): boolean {
    return this.roleMappings.some(
      (mapping) =>
        Number(mapping.menuId) === Number(menuId) &&
        mapping.isDeleted === 0 &&
        mapping.isActive === 1,
    );
  }

  // ============================================================
  // ASSIGN / UPDATE MENU
  // ============================================================

  assignMenu(): void {
    if (!this.selectedModuleName) {
      this.toastr.warning("Please select a module.");

      return;
    }

    /*
     * IMPORTANT FIX:
     * Get actual RoleId from selected Role object.
     */
    const role = this.selectedRole;

    if (!role || Number(role.roleId) <= 0) {
      this.toastr.warning("Please select a valid role.");

      return;
    }

    const roleId = Number(role.roleId);

    /*
     * Extra safety:
     * selected role must belong to selected module.
     */
    if (!this.roleMatchesSelectedModule(role, this.selectedModuleName)) {
      this.toastr.warning("Selected role does not belong to selected module.");

      return;
    }

    if (this.selectedParentMenuId === 0) {
      this.toastr.warning("Please select a parent menu or N/A.");

      return;
    }

    if (this.selectedParentMenuId === -1 && this.selectedSubMenuId <= 0) {
      this.toastr.warning("Please select a sub menu.");

      return;
    }

    console.log("============================================");

    console.log(
      this.isEditMode ? "UPDATE ROLE MENU MAPPING" : "CREATE ROLE MENU MAPPING",
    );

    console.log("Module:", this.selectedModuleName);

    console.log("Role:", role.roleName);

    console.log("ACTUAL ROLE ID:", roleId);

    console.log("Selected Parent MenuId:", this.selectedParentMenuId);

    console.log("Selected Sub MenuId:", this.selectedSubMenuId);

    console.log("============================================");

    const menuIds = new Set<number>();

    // ==========================================================
    // PARENT
    // ==========================================================

    if (this.selectedParentMenuId > 0) {
      menuIds.add(Number(this.selectedParentMenuId));
    }

    // ==========================================================
    // SUBMENU
    // ==========================================================

    if (this.selectedSubMenuId > 0) {
      const selectedSubMenu = this.menus.find(
        (menu) => Number(menu.menuId) === Number(this.selectedSubMenuId),
      );

      /*
       * Automatically assign real parent of selected submenu.
       */
      if (selectedSubMenu && Number(selectedSubMenu.parentMenuId || 0) > 0) {
        menuIds.add(Number(selectedSubMenu.parentMenuId));
      }

      menuIds.add(Number(this.selectedSubMenuId));
    }

    if (menuIds.size === 0) {
      this.toastr.warning("Please select a menu.");

      return;
    }

    // ==========================================================
    // EDIT
    // ==========================================================

    if (this.isEditMode) {
      this.updateMenuMapping(roleId, Array.from(menuIds));

      return;
    }

    // ==========================================================
    // CREATE
    // ==========================================================

    this.createMenuMappings(roleId, Array.from(menuIds));
  }

  // ============================================================
  // CREATE MENU MAPPINGS
  // ============================================================

  private createMenuMappings(roleId: number, menuIds: number[]): void {
    const requests: any[] = [];

    menuIds.forEach((menuId) => {
      const existing = this.roleMappings.find(
        (mapping) =>
          Number(mapping.roleId) === Number(roleId) &&
          Number(mapping.menuId) === Number(menuId) &&
          mapping.isDeleted === 0,
      );

      /*
       * Already active.
       */
      if (existing && existing.isActive === 1) {
        return;
      }

      /*
       * Existing but inactive.
       */
      if (existing) {
        requests.push(
          this.service.updateSubRole({
            subRoleId: existing.subRoleId,

            isActive: 1,

            isDeleted: 0,

            actionUser: this.getActionUser(),
          }),
        );

        return;
      }

      /*
       * NEW MAPPING
       *
       * IMPORTANT:
       * ACTUAL SELECTED RoleId SENT HERE.
       */
      const request = {
        roleId: Number(roleId),

        menuId: Number(menuId),

        actionUser: this.getActionUser(),
      };

      console.log("SubRole Create Request:", request);

      requests.push(this.service.createSubRole(request));
    });

    if (requests.length === 0) {
      this.toastr.info("Selected menu is already assigned to this role.");

      return;
    }

    this.saving = true;

    forkJoin(requests)
      .pipe(
        finalize(() => {
          this.saving = false;
        }),
      )
      .subscribe({
        next: () => {
          this.toastr.success("Menu assigned successfully.");

          this.clearMenuSelection();

          this.loadRoleMappings();
        },

        error: (error) => {
          console.error("SubRole Create Error:", error);

          this.showError(error, "Unable to assign menu.");
        },
      });
  }

  // ============================================================
  // EDIT MAPPING
  // ============================================================

  editMapping(row: MappingView): void {
    const role = this.roles.find(
      (item) => Number(item.roleId) === Number(row.roleId),
    );

    if (!role) {
      this.toastr.warning("Role information was not found.");

      return;
    }

    /*
     * MINIMUM FIX:
     * Patch the module using the same display value used by moduleOptions.
     * Example: SPE / SCL -> School, HM -> Hospital, SM -> Society.
     *
     * Earlier we directly used role.moduleName. If backend returned SPE/SCL/HM/SM,
     * the Module <select> had no matching option, so edit mode visually showed
     * blank Module/Role/Parent/Sub Menu even though ids were available.
     */
    const editModuleName = this.getModuleOptionName(role);

    if (!editModuleName) {
      this.toastr.warning("Unable to resolve module for this role.");

      return;
    }

    this.selectedModuleName = editModuleName;

    this.selectedRoleId = Number(role.roleId);

    this.editingSubRoleId = Number(row.subRoleId);

    this.editingOriginalMenuId = Number(row.menuId);

    this.editingOriginalParentMenuId = Number(row.parentMenuId || 0);

    /*
     * MINIMUM EDIT FIX:
     * Always resolve the currently mapped MenuId from MenuMaster first.
     *
     * - If it is a parent menu, patch that parent exactly as before.
     * - If it is a submenu and its real parent exists, patch Parent + Sub Menu.
     * - If the submenu's parent is not available in MenuMaster, patch Parent as
     *   N/A and still patch the exact submenu stored in DB.
     */
    const editMenu = this.menus.find(
      (menu) => Number(menu.menuId) === Number(row.menuId),
    );

    const actualParentMenuId = Number(
      editMenu?.parentMenuId ?? row.parentMenuId ?? 0,
    );

    if (actualParentMenuId === 0) {
      this.selectedParentMenuId = Number(row.menuId);
      this.selectedSubMenuId = 0;
    } else {
      const actualParent = this.menus.find(
        (menu) =>
          Number(menu.menuId) === Number(actualParentMenuId) &&
          Number(menu.parentMenuId || 0) === 0 &&
          menu.isDeleted === 0,
      );

      this.selectedParentMenuId = actualParent
        ? Number(actualParentMenuId)
        : -1;

      this.selectedSubMenuId = Number(row.menuId);
    }

    console.log("============================================");

    console.log("Edit Mapping Patched:");

    console.log("Module:", this.selectedModuleName);

    console.log("Role:", role.roleName);

    console.log("RoleId:", role.roleId);

    console.log("Parent MenuId:", this.selectedParentMenuId);

    console.log("Sub MenuId:", this.selectedSubMenuId);

    console.log("============================================");

    window.scrollTo({
      top: 0,

      behavior: "smooth",
    });
  }

  // ============================================================
  // UPDATE MENU MAPPING
  //
  // SubRoles_Update cannot change RoleId/MenuId,
  // therefore we preserve existing backend flow:
  //
  // 1. Create/reactivate new mapping.
  // 2. Remove old mapping if menu changed.
  // ============================================================

  private updateMenuMapping(roleId: number, menuIds: number[]): void {
    const requests: any[] = [];

    menuIds.forEach((menuId) => {
      const existing = this.roleMappings.find(
        (mapping) =>
          Number(mapping.roleId) === Number(roleId) &&
          Number(mapping.menuId) === Number(menuId) &&
          mapping.isDeleted === 0,
      );

      if (existing && existing.isActive === 1) {
        return;
      }

      if (existing) {
        requests.push(
          this.service.updateSubRole({
            subRoleId: existing.subRoleId,

            isActive: 1,

            isDeleted: 0,

            actionUser: this.getActionUser(),
          }),
        );

        return;
      }

      const createRequest = {
        /*
         * IMPORTANT FIX:
         * actual selected RoleId.
         */
        roleId: Number(roleId),

        menuId: Number(menuId),

        actionUser: this.getActionUser(),
      };

      console.log("SubRole Edit -> Create Request:", createRequest);

      requests.push(this.service.createSubRole(createRequest));
    });

    /*
     * Remove old mapping only when user changed the menu.
     */
    const selectedTargetMenuId =
      this.selectedSubMenuId > 0
        ? Number(this.selectedSubMenuId)
        : Number(this.selectedParentMenuId);

    if (
      this.editingSubRoleId > 0 &&
      this.editingOriginalMenuId > 0 &&
      selectedTargetMenuId > 0 &&
      Number(selectedTargetMenuId) !== Number(this.editingOriginalMenuId)
    ) {
      requests.push(
        this.service.deleteSubRole({
          subRoleId: Number(this.editingSubRoleId),

          actionUser: this.getActionUser(),
        }),
      );
    }

    /*
     * Same mapping selected.
     */
    if (requests.length === 0) {
      this.toastr.info("No changes found.");

      this.cancelEdit();

      return;
    }

    this.saving = true;

    forkJoin(requests)
      .pipe(
        finalize(() => {
          this.saving = false;
        }),
      )
      .subscribe({
        next: () => {
          this.toastr.success("Menu mapping updated successfully.");

          this.cancelEdit(false);

          this.loadRoleMappings();
        },

        error: (error) => {
          console.error("SubRole Update Mapping Error:", error);

          this.showError(error, "Unable to update menu mapping.");
        },
      });
  }

  // ============================================================
  // CANCEL EDIT
  // ============================================================

  cancelEdit(resetRole: boolean = false): void {
    this.editingSubRoleId = 0;

    this.editingOriginalMenuId = 0;

    this.editingOriginalParentMenuId = 0;

    this.clearMenuSelection();

    if (resetRole) {
      this.selectedRoleId = 0;

      this.roleMappings = [];
    }
  }

  // ============================================================
  // CLEAR MENU SELECTION
  // ============================================================

  private clearMenuSelection(): void {
    this.selectedParentMenuId = 0;

    this.selectedSubMenuId = 0;
  }

  // ============================================================
  // ENABLE / DISABLE
  // ============================================================

  toggleMapping(row: MappingView): void {
    const nextStatus = row.isActive === 1 ? 0 : 1;

    this.service
      .updateSubRole({
        subRoleId: row.subRoleId,

        isActive: nextStatus,

        isDeleted: 0,

        actionUser: this.getActionUser(),
      })
      .subscribe({
        next: () => {
          this.toastr.success(
            nextStatus === 1
              ? "Menu mapping enabled."
              : "Menu mapping disabled.",
          );

          /*
           * MINIMUM FIX:
           * Refresh the complete application after enable/disable.
           * HomeComponent already calls SubRoles/ReadByUserId during init and
           * rebuilds the sidebar menu, so this guarantees the latest SubRole
           * permission is reflected immediately without requiring the user to
           * manually refresh the browser.
           */
          setTimeout(() => {
            window.location.reload();
          }, 250);
        },

        error: (error) => {
          this.showError(error, "Unable to update mapping.");
        },
      });
  }

  // ============================================================
  // DELETE MAPPING
  // ============================================================

  removeMapping(row: MappingView): void {
    /*
     * If removing Parent Menu,
     * protect parent while it still has active child mappings.
     */
    if (Number(row.parentMenuId || 0) === 0) {
      const activeChildren = this.mappedMenus.filter(
        (item) =>
          Number(item.parentMenuId) === Number(row.menuId) &&
          item.isActive === 1 &&
          item.isDeleted === 0,
      );

      if (activeChildren.length > 0) {
        this.toastr.warning(
          "This parent menu has assigned sub menus. Remove the sub menus first.",
        );

        return;
      }
    }

    const confirmed = window.confirm(
      `Remove "${row.menuName}" from this role?`,
    );

    if (!confirmed) {
      return;
    }

    this.service
      .deleteSubRole({
        subRoleId: row.subRoleId,

        actionUser: this.getActionUser(),
      })
      .subscribe({
        next: () => {
          this.toastr.success("Menu mapping removed successfully.");

          if (Number(this.editingSubRoleId) === Number(row.subRoleId)) {
            this.cancelEdit();
          }

          this.loadRoleMappings();
        },

        error: (error) => {
          this.showError(error, "Unable to remove mapping.");
        },
      });
  }

  // ============================================================
  // MENU PARENT NAME
  // ============================================================

  getMenuParentName(parentMenuId: number): string {
    if (Number(parentMenuId || 0) <= 0) {
      return "Root";
    }

    return (
      this.menus.find((menu) => Number(menu.menuId) === Number(parentMenuId))
        ?.menuName || "Parent Menu"
    );
  }

  // ============================================================
  // ROLE MODULE MATCH
  //
  // MINIMUM FIX:
  // First match Role.ModuleName.
  // If older role data does not have ModuleName populated,
  // fall back to GroupName.
  // This only affects the Role dropdown list.
  // ============================================================

  private roleMatchesSelectedModule(
    role: RoleItem,
    selectedModuleName: string,
  ): boolean {
    const selectedModule = this.normalizeModuleName(selectedModuleName);

    if (!selectedModule) {
      return false;
    }

    const roleModule = this.normalizeModuleName(role.moduleName);

    if (roleModule && roleModule === selectedModule) {
      return true;
    }

    const roleGroup = this.normalizeModuleName(role.groupName);

    return !!roleGroup && roleGroup === selectedModule;
  }

  // ============================================================
  // MODULE MATCH
  // ============================================================

  private moduleMatches(
    itemModuleName: string,
    selectedModuleName: string,
  ): boolean {
    const itemModule = this.normalizeModuleName(itemModuleName);

    const selectedModule = this.normalizeModuleName(selectedModuleName);

    if (!selectedModule) {
      return false;
    }

    if (selectedModule === "common") {
      return !itemModule || itemModule === "common" || itemModule === "global";
    }

    return itemModule === selectedModule;
  }

  // ============================================================
  // MODULE OPTION NAME FOR EDIT MODE
  //
  // MINIMUM FIX:
  // Converts backend module aliases to the exact values used by
  // moduleOptions so Angular can patch the Module dropdown correctly.
  // ============================================================

  private getModuleOptionName(role: RoleItem): string {
    const candidates = [
      role?.moduleName,
      role?.groupName,
    ];

    for (const candidate of candidates) {
      const normalized = this.normalizeModuleName(candidate);

      if (normalized === "school") {
        return "School";
      }

      if (normalized === "hospital") {
        return "Hospital";
      }

      if (normalized === "society") {
        return "Society";
      }

      if (normalized === "common") {
        return "Common";
      }
    }

    return "";
  }

  // ============================================================
  // MODULE NORMALIZER
  // ============================================================

  private normalizeModuleName(value: string): string {
    const moduleName = String(value || "")
      .trim()
      .toLowerCase();

    if (
      moduleName === "school" ||
      moduleName === "spe" ||
      moduleName === "scl"
    ) {
      return "school";
    }

    if (
      moduleName === "hospital" ||
      moduleName === "hm"
    ) {
      return "hospital";
    }

    if (
      moduleName === "society" ||
      moduleName === "housing society" ||
      moduleName === "sm"
    ) {
      return "society";
    }

    if (
      moduleName === "common" ||
      moduleName === "global"
    ) {
      return "common";
    }

    return moduleName;
  }

  // ============================================================
  // EXTRACT ARRAY
  // ============================================================

  private extractArray(response: any): any[] {
    if (!response) {
      return [];
    }

    if (Array.isArray(response)) {
      return response;
    }

    const candidates = [
      response?.items,
      response?.Items,

      response?.data,
      response?.Data,

      response?.data?.items,
      response?.data?.Items,

      response?.Data?.items,
      response?.Data?.Items,

      response?.result,
      response?.Result,

      response?.result?.items,
      response?.result?.Items,

      response?.Result?.items,
      response?.Result?.Items,

      response?.responseData,
      response?.ResponseData,

      response?.responseData?.items,
      response?.responseData?.Items,

      response?.ResponseData?.items,
      response?.ResponseData?.Items,

      response?.list,
      response?.List,

      response?.value,
      response?.Value,
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }

    const deepCandidates = [
      response?.items?.data,

      response?.items?.items,

      response?.data?.data,

      response?.result?.data,

      response?.responseData?.data,
    ];

    for (const candidate of deepCandidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }

    return [];
  }

  // ============================================================
  // NORMALIZE ROLE
  // ============================================================

  private normalizeRole(item: any): RoleItem {
    return {
      roleId: Number(item?.roleId ?? item?.RoleId ?? 0),

      roleName: String(item?.roleName ?? item?.RoleName ?? ""),

      roleCode: String(item?.roleCode ?? item?.RoleCode ?? ""),

      roleDesc: String(item?.roleDesc ?? item?.RoleDesc ?? ""),

      groupName: String(item?.groupName ?? item?.GroupName ?? ""),

      moduleName: String(item?.moduleName ?? item?.ModuleName ?? ""),

      moduleId: Number(item?.moduleId ?? item?.ModuleId ?? 0),

      isActive: Number(item?.isActive ?? item?.IsActive ?? 1),

      isDeleted: Number(item?.isDeleted ?? item?.IsDeleted ?? 0),
    };
  }

  // ============================================================
  // NORMALIZE MENU
  // ============================================================

  private normalizeMenu(item: any): MenuItem {
    return {
      menuId: Number(item?.menuId ?? item?.MenuId ?? 0),

      menuName: String(item?.menuName ?? item?.MenuName ?? ""),

      menuCode: String(item?.menuCode ?? item?.MenuCode ?? ""),

      menuDesc: String(item?.menuDesc ?? item?.MenuDesc ?? ""),

      displayOrder: Number(item?.displayOrder ?? item?.DisplayOrder ?? 0),

      parentMenuId: Number(item?.parentMenuId ?? item?.ParentMenuId ?? 0),

      defaultChildMenuId: Number(
        item?.defaultChildMenuId ?? item?.DefaultChildMenuId ?? 0,
      ),

      menuIconUrl: String(item?.menuIconUrl ?? item?.MenuIconUrl ?? ""),

      templatePath: String(item?.templatePath ?? item?.TemplatePath ?? ""),

      moduleName: String(item?.moduleName ?? item?.ModuleName ?? ""),

      moduleId: Number(item?.moduleId ?? item?.ModuleId ?? 0),

      isActive: Number(item?.isActive ?? item?.IsActive ?? 1),

      isDeleted: Number(item?.isDeleted ?? item?.IsDeleted ?? 0),
    };
  }

  // ============================================================
  // NORMALIZE SUB ROLE
  // ============================================================

  private normalizeSubRole(item: any): SubRoleItem {
    return {
      subRoleId: Number(item?.subRoleId ?? item?.SubRoleId ?? 0),

      roleId: Number(item?.roleId ?? item?.RoleId ?? 0),

      subRoleName: String(item?.subRoleName ?? item?.SubRoleName ?? ""),

      subRoleCode: String(item?.subRoleCode ?? item?.SubRoleCode ?? ""),

      subRoleDesc: String(item?.subRoleDesc ?? item?.SubRoleDesc ?? ""),

      menuId: Number(item?.menuId ?? item?.MenuId ?? 0),

      displayOrder: Number(item?.displayOrder ?? item?.DisplayOrder ?? 0),

      isDefault: Number(item?.isDefault ?? item?.IsDefault ?? 0),

      isActive: Number(item?.isActive ?? item?.IsActive ?? 1),

      isDeleted: Number(item?.isDeleted ?? item?.IsDeleted ?? 0),
    };
  }

  // ============================================================
  // ACTION USER
  // ============================================================

  private getActionUser(): string {
    const actionUser = (
      localStorage.getItem("userId") ||
      localStorage.getItem("UserId") ||
      "system"
    )
      .replace(/^"|"$/g, "")
      .trim();

    return actionUser || "system";
  }

  // ============================================================
  // ERROR
  // ============================================================

  private showError(error: any, fallback: string): void {
    let message =
      error?.error?.message ??
      error?.error?.Message ??
      error?.error?.title ??
      error?.error?.Title ??
      error?.message ??
      fallback;

    if (typeof error?.error === "string" && error.error.trim()) {
      message = error.error.trim();
    }

    this.toastr.error(String(message || fallback));
  }
}
